import React from "react";
import "../css/DesignSystem.css";
import {
    BCHeader1,
    BCHeader2,
    BCHeader3,
    BCHeader4,
    BCHeader5,
    BCHeader6,
    Subtitle1,
    Subtitle2,
    Body1,
    Body2,
    Body3,
    Caption
} from "../components/generic/BCHeaders";
import {BCIcon, ICONS} from "../components/generic/BCIcons";
import {FlexCol, FlexRow, StandardPage} from "../components/generic/Containers";
import {
    BCPrimaryButton,
    BCSecondaryButton,
    BCUnderlineButton
} from "../components/generic/BCButtons";
import {getMockOnboardingFlow} from "../testUtils/MockApi";
import {OnboardingQuestionV2} from "../components/onboarding/OnboardingQuestionV2";

export const DesignSystem: React.FC = () => {
    return (
        <StandardPage>
            <FlexCol gap={2}>
                <DesignComponentSection title="Typography">
                    <Typography />
                </DesignComponentSection>
                <DesignComponentSection title="Color Palette">
                    <ColorPalette />
                </DesignComponentSection>
                <DesignComponentSection title="Iconography">
                    <Iconography />
                </DesignComponentSection>
                <DesignComponentSection title="Buttons">
                    <Buttons />
                </DesignComponentSection>
                <DesignComponentSection title={"Onboarding Flow"}>
                    <OnboardingComponents />
                </DesignComponentSection>
            </FlexCol>
        </StandardPage>
    );
};

interface DesignComponentSectionProps {
    title: string;
    children: React.ReactNode;
}

export const DesignComponentSection: React.FC<DesignComponentSectionProps> = ({ title, children }) => {
    return (
        <FlexCol gap={.5}>
            <BCHeader2 text={title} />
            <div className="design-system-section-content">
                {children}
            </div>
        </FlexCol>
    );
};

export const Typography: React.FC = () => {
    return (
        <div className={'typography-rows'}>
            <div className={'typography-column'}>
                <BCHeader1 text="Heading 1" />
                <BCHeader2 text="Heading 2" />
                <BCHeader3 text="Heading 3" />
                <BCHeader4 text="Heading 4" />
                <BCHeader5 text="Heading 5" />
                <BCHeader6 text="Heading 6" />
            </div>

            <div className={'typography-column'}>
                <Subtitle1 text="Subtitle 1" />
                <Subtitle2 text="Subtitle 2" />
            </div>

            <div className={'typography-column'}>
                <Body1 text="Body 1 - Regular paragraph or content text." />
                <Body2 text="Body 2 - Slightly smaller content or subcopy." />
                <Body3 text="Body 3 - Small supporting text." />

                <Caption text="Caption - fine print or helper text." />
            </div>

        </div>
    );
};

export const ColorPalette: React.FC = () => {
    const colorGroups = [
        {
            title: "Primary Colors (--color-primary-###)",
            prefix: "primary",
            swatches: [950, 900, 800, 700, 600, 500, 400, 300, 200, 100, 50],
        },
        {
            title: "Accent Colors (--color-accent-###)",
            prefix: "accent",
            swatches: [950, 900, 800, 700, 600, 500, 400, 300, 200, 100, 50],
        },
        {
            title: "Grayscale (--color-gray-###)",
            prefix: "gray",
            swatches: [950, 900, 800, 700, 600, 500, 400, 300, 200, 100, 50],
        }
    ];

    return (
        <div className="color-palette-container">
            {colorGroups.map((group) => (
                <div key={group.title} className="color-group">
                    <BCHeader3 text={group.title} />
                    <div className="color-row">
                        {group.swatches.map((shade) => {
                            const varName = `--color-${group.prefix}-${shade}`;
                            const color = getComputedStyle(document.documentElement).getPropertyValue(varName).trim();
                            return (
                                <div
                                    key={shade}
                                    className="color-swatch"
                                    style={{ backgroundColor: color }}
                                >
                                    <span className="swatch-label">{shade}</span>
                                    <span className="swatch-hex">{color}</span>
                                </div>
                            );
                        })}
                    </div>
                </div>
            ))}
        </div>
    );
};

export const Iconography: React.FC = () => {
    return (
        <div className="iconography-container">
            <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-6 gap-6">
                {Object.entries(ICONS).map(([name, Icon]) => (
                    <div key={name} className="flex flex-col items-center text-center gap-2">
                        <BCIcon icon={Icon} className="text-3xl text-gray-700" />
                        <span className="text-xs text-gray-500">{name}</span>
                    </div>
                ))}
            </div>
        </div>
    );
};

export const Buttons: React.FC = () => {
    return (
        <FlexCol gap={1}>
            <FlexRow gap={1}>
                <BCPrimaryButton text="Primary Button" onClick={() => {}}/>
                <BCPrimaryButton text="Primary Button" onClick={() => {}} iconLeft={ICONS.LEFT_ARROW}/>
                <BCPrimaryButton text="Primary Button" onClick={() => {}} iconRight={ICONS.RIGHT_ARROW}/>
            </FlexRow>
            <FlexRow gap={1}>
                <BCSecondaryButton text="Secondary Button" onClick={() => {}}/>
                <BCSecondaryButton text="Secondary Button" onClick={() => {}} iconLeft={ICONS.LEFT_ARROW}/>
                <BCSecondaryButton text="Secondary Button" onClick={() => {}} iconRight={ICONS.RIGHT_ARROW}/>
            </FlexRow>
            <FlexRow gap={1}>
                <BCUnderlineButton text="Underline Button" onClick={() => {}}/>
                <BCUnderlineButton text="Underline Button" onClick={() => {}} iconLeft={ICONS.LEFT_ARROW}/>
                <BCUnderlineButton text="Underline Button" onClick={() => {}} iconRight={ICONS.RIGHT_ARROW}/>
            </FlexRow>
            {/*<FlexRow gap={1}>*/}
            {/*    <BCQuizAnswerButton text="Quiz Button" onClick={() => {}}/>*/}
            {/*    <BCQuizAnswerButton text="Quiz Button" onClick={() => {}} iconLeft={ICONS.LEFT_ARROW}/>*/}
            {/*    <BCQuizAnswerButton text="Quiz Button" onClick={() => {}} iconRight={ICONS.RIGHT_ARROW}/>*/}
            {/*</FlexRow>*/}
        </FlexCol>
    );
};

export const OnboardingComponents: React.FC = () => {
    const mockOnboardingFlow = getMockOnboardingFlow()
    return (
        <FlexCol>
            <OnboardingQuestionV2 question={mockOnboardingFlow.questions[0]} setNextDisabled={() => {}}/>
        </FlexCol>
    )
}